import React, { useEffect, useState, useCallback } from "react";
import {
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Download,
  ExternalLink,
  ImageIcon,
} from "lucide-react";
import {
  openSafeAttachmentUrl,
  downloadAttachment,
} from "../utils/attachmentUtils";

export interface ImageLightboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  imageName?: string;
  issueId?: string;
}

export const ImageLightboxModal: React.FC<ImageLightboxModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageName = "Screenshot / Attachment",
  issueId,
}) => {
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  // Reset zoom when image changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setZoomLevel(1);
    }
  }, [isOpen, imageUrl]);

  // Handle keyboard shortcuts (Esc to close, +/-/0 for zoom)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setZoomLevel((prev) => Math.min(prev + 0.25, 3));
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
      } else if (e.key === "0") {
        e.preventDefault();
        setZoomLevel(1);
      }
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen || !imageUrl) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const downloadFilename = issueId
    ? `${issueId}_${imageName.replace(/[^a-zA-Z0-9.-]/g, "_")}`
    : imageName.replace(/[^a-zA-Z0-9.-]/g, "_");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image Preview Lightbox"
      onClick={handleBackdropClick}
      className="fixed inset-0 z-60 flex flex-col items-center justify-between p-3 sm:p-6 bg-slate-950/85 backdrop-blur-md animate-in fade-in duration-200 select-none"
    >
      {/* Lightbox Header Toolbar */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl flex items-center justify-between gap-3 px-4 py-3 bg-slate-900/90 text-white rounded-xl border border-slate-800 shadow-xl z-10 shrink-0"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="p-1.5 rounded-lg bg-teal-500/20 text-teal-400 shrink-0">
            <ImageIcon className="w-4 h-4" />
          </div>
          <div className="truncate">
            <h3 className="text-sm font-semibold truncate text-slate-100">
              {imageName}
            </h3>
            {issueId && (
              <p className="text-[10px] text-teal-400 font-mono font-bold">
                Ticket: {issueId}
              </p>
            )}
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Zoom Out */}
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.5}
            title="Zoom out (-)"
            aria-label="Zoom out"
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent transition cursor-pointer"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          {/* Zoom Percentage */}
          <button
            type="button"
            onClick={handleResetZoom}
            title="Reset zoom (0)"
            aria-label="Reset zoom to 100%"
            className="px-2 py-1 rounded-md text-xs font-mono font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition cursor-pointer"
          >
            {Math.round(zoomLevel * 100)}%
          </button>

          {/* Zoom In */}
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 3}
            title="Zoom in (+)"
            aria-label="Zoom in"
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent transition cursor-pointer"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <div className="h-4 w-px bg-slate-700 mx-1" />

          {/* Reset Zoom */}
          <button
            type="button"
            onClick={handleResetZoom}
            title="Reset scale"
            aria-label="Reset scale"
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition cursor-pointer"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Download */}
          <button
            type="button"
            onClick={() => downloadAttachment(imageUrl, downloadFilename)}
            title="Download image"
            aria-label="Download image"
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition cursor-pointer"
          >
            <Download className="w-4 h-4" />
          </button>

          {/* Open in new tab (Safe URL) */}
          <button
            type="button"
            onClick={() => openSafeAttachmentUrl(imageUrl, downloadFilename)}
            title="Open in new tab"
            aria-label="Open image in new browser tab"
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition cursor-pointer"
          >
            <ExternalLink className="w-4 h-4" />
          </button>

          <div className="h-4 w-px bg-slate-700 mx-1" />

          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            title="Close preview (Esc)"
            aria-label="Close preview"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-rose-500/20 hover:text-rose-400 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Image Viewport with fluid transition */}
      <div className="flex-1 w-full max-w-6xl flex items-center justify-center p-2 sm:p-4 overflow-auto min-h-0">
        <div
          className="relative max-h-full max-w-full flex items-center justify-center transition-transform duration-150 ease-out"
          style={{ transform: `scale(${zoomLevel})` }}
        >
          <img
            src={imageUrl}
            alt={imageName}
            className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl border border-slate-800/80 pointer-events-auto"
          />
        </div>
      </div>

      {/* Bottom Hint */}
      <div className="text-[11px] text-slate-400 py-1 tracking-wide font-medium flex items-center gap-3">
        <span>
          Click outside or press{" "}
          <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] font-mono">
            Esc
          </kbd>{" "}
          to close
        </span>
        <span>•</span>
        <span>
          Use{" "}
          <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] font-mono">
            +
          </kbd>{" "}
          /{" "}
          <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] font-mono">
            -
          </kbd>{" "}
          to zoom
        </span>
      </div>
    </div>
  );
};
